import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import {
  rittecThemes,
  experimentelleThemes,
  isExperimentalTheme,
  resolveAllowedTheme,
  applyColorScheme,
  useExperimentalThemeAccess,
  type RittecTheme,
  type ExperimentellesTheme,
  type RittecThemeName,
  type ExperimentellesThemeName,
} from '@rittec/themes';

/** Name eines wählbaren Themes — Grundthema (light/dark) oder experimentelles Theme.
 *  Kommt jetzt aus @rittec/themes; hier nur als Vereinigung re-exportiert, damit
 *  bestehende Importe (`import type { ThemeName }`) weiterlaufen. */
export type ThemeName = RittecThemeName | ExperimentellesThemeName;

/** Gemeinsame Beschreibungsform für Menü-Einträge (Grund + experimentell). */
export type ThemeOption = RittecTheme | ExperimentellesTheme;

/** localStorage-Schlüssel für das gewählte Theme. */
const THEME_KEY = 'pm_theme';
/** localStorage-Schlüssel für die Freischaltung der experimentellen Themes. */
const UNLOCK_KEY = 'pm_experimental_themes_unlocked';

/** Alle gültigen Themenamen (Grund + experimentell) — für die Wiederherstellung
 *  aus localStorage. */
const ALL_NAMES = new Set<ThemeName>([
  ...rittecThemes.map((t) => t.name),
  ...experimentelleThemes.map((t) => t.name),
]);

interface ThemeContextType {
  /** Aktuell gewähltes Theme. */
  theme: ThemeName;
  /** Theme direkt setzen. */
  setTheme: (name: ThemeName) => void;
  /** Grundthemes (light/dark) — immer wählbar. */
  baseThemes: readonly RittecTheme[];
  /** Experimentelle Themes — nur anzeigen, wenn freigeschaltet. */
  experimentalThemes: readonly ExperimentellesTheme[];
  /** Ob die experimentellen Themes freigeschaltet sind. */
  experimentalUnlocked: boolean;
  /** Freischaltung der experimentellen Themes umschalten. */
  toggleExperimental: () => void;
  /** Rückwärtskompatibel: identisch zu `theme`. */
  mode: ThemeName;
  /** Rückwärtskompatibel: schaltet zwischen Light und Dark um. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    return stored && ALL_NAMES.has(stored as ThemeName) ? (stored as ThemeName) : 'light';
  });

  const [experimentalUnlocked, setExperimentalUnlocked] = useState<boolean>(
    () => localStorage.getItem(UNLOCK_KEY) === 'true',
  );

  // Freischaltungs-Baustein aus @rittec/themes. Hält keinen eigenen Speicher —
  // der Zustand liegt bei uns (localStorage), das Paket liefert nur die Logik.
  const access = useExperimentalThemeAccess({
    unlocked: experimentalUnlocked,
    onUnlockedChange: (value) => {
      setExperimentalUnlocked(value);
      try { localStorage.setItem(UNLOCK_KEY, String(value)); } catch { /* quota */ }
    },
  });

  // Ist ein experimentelles Theme aktiv, aber (nicht mehr) freigeschaltet, fällt es
  // auf sein Hell/Dunkel-Äquivalent zurück — beim Laden wie beim Sperren.
  useEffect(() => {
    const allowed = resolveAllowedTheme(theme, experimentalUnlocked);
    if (allowed !== theme) setThemeState(allowed);
  }, [theme, experimentalUnlocked]);

  // data-theme setzen + Theme persistieren.
  //
  // Grundthemes (light/dark): applyColorScheme(theme) setzt data-theme auf das Theme
  // selbst; MUI wählt darüber das colorScheme.
  //
  // TEMPORÄR — Weg (b), angelegt 2026-08-25, Vorgang/Branch
  // feature/rittec-themes-migration:
  // Für experimentelle Themes wird data-theme auf das experimentelle Theme SELBST
  // gesetzt (data-theme='matrix' usw.), damit die lokal in themes.css
  // wiederhergestellten html[data-theme='<name>']-Blöcke die --pm-*-Tokens liefern.
  // Das Aussehen der MUI-Komponenten bestimmt weiterhin der Objekttausch im Provider.
  // applyColorScheme tut ausschließlich setAttribute('data-theme', mode) (kein weiterer
  // State), akzeptiert typseitig aber nur Grundthemennamen — für experimentelle Themes
  // wird das Attribut daher direkt gesetzt (mit SSR-Guard analog zum Paket-Baustein).
  // Entfernen, sobald @rittec/themes die --pm-*-CSS-Tokens für die experimentellen
  // Themes selbst mitliefert (Weg a); die Vorlage wurde an die Paket-Pflege geliefert.
  useEffect(() => {
    if (isExperimentalTheme(theme)) {
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', theme);
      }
    } else {
      applyColorScheme(theme);
    }
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* quota */ }
  }, [theme]);

  const setTheme = useCallback((name: ThemeName) => setThemeState(name), []);
  const toggleTheme = useCallback(
    () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  );

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        baseThemes: rittecThemes,
        experimentalThemes: experimentelleThemes,
        experimentalUnlocked,
        toggleExperimental: access.toggle,
        mode: theme,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeMode(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
}
